import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import type { IAttachmentRead, IMessage, ISecureInboxSettings, ISecureInboxTopic, IUserMailbox } from '../../api/domain';
import {
    deleteAttachmentSuccess,
    fetchAttachmentContentSuccess,
    fetchAttachmentsSuccess,
    fetchMailboxesFailure,
    fetchMailboxesSuccess,
    fetchMessagesSuccess,
    fetchMessageSuccess,
    fetchSettingsSuccess,
    fetchTopicsSuccess,
    fetchUnreadCountSuccess,
    resolveRmForMailboxesSuccess,
    updateMessage,
} from './secure-inbox-api-actions';
import { getZipTypeVariants } from '../../utils/mime-util';

interface ISecureInboxApiState {
    attachments: IAttachmentRead[];
    attachmentFileSize: Record<string, number>;
    topics: ISecureInboxTopic[];
    message?: IMessage;
    mailboxes: IUserMailbox[];
    messages: IMessage[];
    totalCount: number;
    unreadMessagesCount: number;
    settings: ISecureInboxSettings;
}
export type { ISecureInboxApiState };

export const initialState: ISecureInboxApiState = {
    attachments: [],
    attachmentFileSize: {},
    topics: [],
    messages: [],
    totalCount: 0,
    mailboxes: [],
    unreadMessagesCount: 0,
    settings: {
        senderAddressWhitelistRegex: '',
        moduleEnabled: false,
        helpcenterAddress: '',
    },
};

export const secureInboxApiReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(fetchSettingsSuccess, (state, { payload: settings }) => {
            // TODO: OBB-874: Once the product team implements the other media types, remove this whole conditional part
            const supportedAttachmentMediaTypes = settings.supportedAttachmentMediaTypes ?? [];
            if (supportedAttachmentMediaTypes.some((mediaType) => mediaType === 'application/zip')) {
                supportedAttachmentMediaTypes.push(...Object.keys(getZipTypeVariants()));
            }

            state.settings = { ...settings, supportedAttachmentMediaTypes };
        })
        .addCase(fetchTopicsSuccess, (state, { payload: topics }) => {
            state.topics = topics;
        })
        .addCase(fetchMailboxesSuccess, (state, { payload: mailboxes }) => {
            state.mailboxes = mailboxes;
        })
        .addCase(fetchMailboxesFailure, (state) => {
            state.mailboxes = [];
        })
        .addCase(resolveRmForMailboxesSuccess, (state, { payload: mailboxes }) => {
            state.mailboxes = mailboxes;
        })
        .addCase(fetchMessagesSuccess, (state, { payload: { messages, totalCount } }) => {
            state.messages = messages;
            state.totalCount = totalCount;
        })
        .addCase(fetchUnreadCountSuccess, (state, { payload: unreadMessagesCount }) => {
            state.unreadMessagesCount = unreadMessagesCount;
        })
        .addCase(fetchAttachmentsSuccess, (state, { payload: attachments }) => {
            const copy = [...state.attachments];
            if (attachments.length) {
                const filteredAttachments = attachments.filter(
                    (attachment) => !copy.some((it) => it.messageId === attachment.messageId && it.id === attachment.id),
                );
                copy.push(...filteredAttachments);
            }
            state.attachments = copy;
        })
        .addCase(deleteAttachmentSuccess, (state, { payload: { attachmentId } }) => {
            state.attachments = state.attachments.filter((attachment) => attachment.id !== attachmentId);
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete state.attachmentFileSize[attachmentId];
        })
        .addCase(fetchAttachmentContentSuccess, (state, { payload }) => {
            state.attachmentFileSize[payload.attachmentId] = payload.fileSize;
        })
        .addMatcher(isAnyOf(fetchMessageSuccess, updateMessage), (state, { payload: message }) => {
            state.message = message;
        });
});

export default secureInboxApiReducer;
